import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useRef } from 'react';

import { AuthRedirectError, REDIRECTABLE_ERRORS } from './useAuthedClient';

// Thrown by `useAuthedRest.request()` for non-2xx responses. Carries the
// status and the response body text (the API's error handler returns plain
// text, e.g. "Already on the list" for a duplicate allowlist add) so
// callers can match on either.
export class RestError extends Error {
  readonly status: number;
  constructor(status: number, body: string) {
    super(body || `Request failed with status ${String(status)}`);
    this.name = 'RestError';
    this.status = status;
  }
}

export interface AuthedRest {
  request<T = unknown>(path: string, init?: RequestInit): Promise<T>;
}

// REST counterpart of `useAuthedClient`, for the admin endpoints — those
// are deliberately REST (the /admin/* group 404s for non-admins, a
// property GraphQL's 200-with-errors envelope can't express). Token
// acquisition and the Universal Login redirect handling mirror
// `useAuthedClient`; keep the two in sync.
export function useAuthedRest(): AuthedRest {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // Refs keep the returned object stable across auth0 callback-identity
  // churn, so consuming hooks (#181) don't suffer re-fetch storms when
  // the SDK re-creates its callbacks on internal state transitions.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  const redirectRef = useRef(loginWithRedirect);
  redirectRef.current = loginWithRedirect;

  return useMemo<AuthedRest>(() => {
    // Defer baseUrl computation until first use so module import doesn't
    // require `window` (allows shared imports from non-DOM tools).
    const getBaseUrl = () =>
      (import.meta.env.VITE_API_BASE_URL ?? window.location.origin).replace(/\/+$/, '');

    return {
      request: async <T = unknown>(path: string, init: RequestInit = {}): Promise<T> => {
        let token: string;
        try {
          token = await getTokenRef.current();
        } catch (err) {
          const code = (err as { error?: unknown } | null)?.error;
          if (typeof code === 'string' && REDIRECTABLE_ERRORS.has(code)) {
            // Await the redirect so we know whether the browser is actually
            // navigating; on failure fall through to the original error so
            // the hook layer doesn't pin the UI to a spinner forever.
            try {
              await redirectRef.current();
            } catch (redirectErr) {
              console.error('Auth0 loginWithRedirect failed', redirectErr);
              throw err;
            }
            throw new AuthRedirectError(code);
          }
          throw err;
        }
        // Per-request Headers so concurrent callers cannot interleave
        // header mutation and dispatch with each other's tokens.
        const headers = new Headers(init.headers);
        headers.set('Authorization', `Bearer ${token}`);
        if (init.body !== undefined && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
        const response = await fetch(`${getBaseUrl()}${path}`, { ...init, headers });
        if (!response.ok) {
          throw new RestError(response.status, await response.text());
        }
        if (response.status === 204) {
          return undefined as T;
        }
        return (await response.json()) as T;
      },
    };
  }, []);
}
